/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { checkForUpdates } from '../utils/versionCheck';

const UpdateNotification = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateInfo, setUpdateInfo] = useState({});
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkForAppUpdates = async () => {
      const result = await checkForUpdates();

      if (result.hasUpdate) {
        setUpdateInfo(result);
        setUpdateAvailable(true);
      }
    };

    // Check immediately on mount
    checkForAppUpdates();

    // Check periodically (every 5 minutes)
    const interval = setInterval(checkForAppUpdates, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    // Save current path to restore after reload
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('refresh_return_path', currentPath);

    // Mark the new version as current before refreshing
    if (updateInfo.serverVersion) {
      localStorage.setItem('app_version', updateInfo.serverVersion.toString());
    }

    // Use standard reload (not force reload) to preserve better compatibility
    window.location.reload();
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Auto-show again after 30 minutes
    setTimeout(() => setDismissed(false), 30 * 60 * 1000);
  };

  if (!updateAvailable || dismissed) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md animate-in slide-in-from-top">
      <Card className="border-blue-200 bg-blue-50 shadow-lg">
        <CardContent className="pt-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1 space-y-2">
              <h3 className="font-medium text-blue-900">
                New Version Available
              </h3>
              <p className="text-sm text-blue-700">
                {updateInfo.updateMessage ||
                 "A new version of the app is available with important updates and bug fixes."}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                You'll be returned to this page after updating.
              </p>
              <div className="flex space-x-2 pt-2">
                <Button
                  onClick={handleRefresh}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Update Now
                </Button>
                <Button
                  onClick={handleDismiss}
                  size="sm"
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  Later
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpdateNotification;